// =============================================================================
// Flujo de Fondos — motor único de cálculo
// =============================================================================
// Fuente de verdad única para los importes de un mes (ingresos, egresos, saldo).
// Reemplaza los 4 motores independientes que vivían dentro de FlujoFondos.jsx,
// FlujoFondosMesDetalle.jsx, fetchAnualData y fetchDetalleMensualAgregado, que
// producían números distintos según la vista.
//
// Diseño:
//   - Toda fuente (ventas, gastos, movimientos, compras, proyecciones, cuentas
//     por cobrar, CRM) se normaliza a un único registro `FlowMovement`.
//   - La lógica de transformación/agregación es PURA (sin Supabase) → testeable.
//   - `getFlowMovements` recibe `supabaseClient` por parámetro y hace el fetch.
//
// Reglas canónicas (ver ESPEC_FLUJO_FONDOS_CALCULO_UNICO.md):
//   - Fecha SIEMPRE 'YYYY-MM-DD' en zona Buenos Aires (nunca getDate() sobre TS).
//   - Ventas por fecha de ACREDITACIÓN (settlement), no de emisión.
//   - Dedup EXACTO: el movimiento espejo de un gasto trae documento_tipo='Gasto'
//     + documento_id. Se descarta por vínculo, nunca por monto/fecha.
//   - Compras pendientes = egreso PROYECTADO; compras pagadas las representa su
//     movimiento de pago (real), por eso se excluyen para no duplicar.
//   - Importes SIEMPRE con 2 decimales (centavos). Nunca abreviar con M/K.
// =============================================================================

import { fetchAllPaginated } from './productCatalog.js';
import {
  getSaleSettlementDate,
  getSaleSettlementAmount,
  estimateSettlementLookbackDays,
} from './paymentSettlement.js';

/**
 * @typedef {Object} FlowMovement
 * @property {string} id              Único por origen: `${source}:${sourceRowId}`
 * @property {string} source          'venta'|'gasto'|'movimiento'|'compra'|'proyeccion'|'receivable'|'crm_proyecto'|'crm_hito'|'crm_gasto'
 * @property {'in'|'out'} direction
 * @property {'real'|'proyectado'} state
 * @property {number} amount          Siempre positivo
 * @property {string} date            'YYYY-MM-DD' en zona Buenos Aires
 * @property {string} concept
 * @property {string} category
 * @property {string|null} entityId
 * @property {string|null} entityName
 * @property {string|null} bankId
 * @property {string|number|null} sourceRowId
 * @property {boolean} isReconcilable
 * @property {Object} [meta]          Detalle específico por fuente (ej. compras: dueDate, emissionDate, voucherNumber, voucherType, status)
 */

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

export const LEGACY_CRM_SOURCES = ['crm_hito', 'crm_proyecto', 'crm_ticket', 'crm_schedule'];
const LEGACY_CRM_SOURCE_FILTER = LEGACY_CRM_SOURCES.join(',');

// Definición única en utils/saleStatuses.js. Se re-exportan para no romper a los
// módulos que ya los importaban desde acá.
export { CANCELLED_PURCHASE_STATUSES, CANCELLED_SALE_STATUSES } from './saleStatuses.js';
import { CANCELLED_PURCHASE_STATUSES, CANCELLED_SALE_STATUSES } from './saleStatuses.js';

const PAID_PURCHASE_STATUSES = new Set(['pagada', 'pagado', 'paid']);

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ---------------------------------------------------------------------------
// Helpers de texto / dinero / fecha (puros)
// ---------------------------------------------------------------------------

export const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const normalizeMoney = (n) => {
  const v = Number(n || 0);
  return Number.isFinite(v) ? v : 0;
};

/**
 * Formato único de importes en todo el módulo: es-AR, SIEMPRE 2 decimales.
 * Nunca abrevia con M/K. `withSign` antepone +/-.
 */
export const formatMoney = (n, { withSign = false } = {}) => {
  const value = normalizeMoney(n);
  const abs = Math.abs(value).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!withSign) return `$ ${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const sign = value < 0 ? '-' : '+';
  return `${sign} $ ${abs}`;
};

const toDateOnly = (value) => String(value || '').slice(0, 10);

/**
 * Reduce cualquier valor de fecha (Date string, ISO con hora, 'YYYY-MM-DD') a
 * 'YYYY-MM-DD' en zona America/Argentina/Buenos_Aires. ÚNICA forma permitida de
 * ubicar un movimiento en un día/mes. Evita el corrimiento de medianoche que
 * provoca usar new Date(x).getDate() con la zona del navegador.
 */
export const getBuenosAiresDateOnly = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(raw));
  } catch {
    return raw.slice(0, 10);
  }
};

const shiftDateOnly = (value, days = 0) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return null;
  const base = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  base.setDate(base.getDate() + Number(days || 0));
  return base.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// Clasificadores de fuentes (puros)
// ---------------------------------------------------------------------------

// Las ventas ya se cuentan desde `sales`; sus espejos en movimientos_financieros
// ('Venta', 'Venta Online', ...) NO deben re-sumarse.
const isSaleMirrorCategory = (value) => normalizeText(value).startsWith('venta');

// Dedup EXACTO: el movimiento espejo de un gasto trae documento_tipo='Gasto'.
const isGastoMirrorMovement = (row) => String(row?.documento_tipo || '') === 'Gasto';

const getPurchasePaymentConditionDays = (purchase) => {
  const normalized = normalizeText(purchase?.condicion_pago);
  const matched = normalized.match(/(\d+)/);
  return matched ? Math.max(0, Number(matched[1]) || 0) : 0;
};

const getPurchaseDueDate = (purchase) => {
  const purchaseDate = getBuenosAiresDateOnly(purchase?.fecha || purchase?.created_at);
  if (!purchaseDate) return '';
  return shiftDateOnly(purchaseDate, getPurchasePaymentConditionDays(purchase)) || purchaseDate;
};

const resolveExpenseCategory = (value) => {
  const normalized = normalizeText(value);
  if (normalized.includes('fijo')) return 'Fijo';
  if (normalized.includes('variable')) return 'Variable';
  return '';
};

export const getExpenseCategoryLabel = (value) => {
  const category = resolveExpenseCategory(value);
  return category ? `Gasto ${category}` : 'Gasto';
};

const firstOf = (relation) => (Array.isArray(relation) ? relation[0] : relation);

/**
 * ¿La venta cuenta como ingreso del flujo? Excluye anuladas/canceladas (en
 * cualquier variante de estado), ventas desactivadas (active=false) y
 * presupuestos. Antes no se filtraba nada y una venta anulada o un presupuesto
 * seguía sumando como ingreso real del mes.
 */
export const isFlowRelevantSale = (sale) => {
  if (!sale) return false;
  if (sale.active === false) return false;
  if (CANCELLED_SALE_STATUSES.has(normalizeText(sale.status))) return false;
  const invoiceStatus = normalizeText(sale.invoice_status);
  if (CANCELLED_SALE_STATUSES.has(invoiceStatus)) return false;
  if (invoiceStatus === 'presupuesto') return false;
  // Venta administrativa a Consumidor Final "sin movimiento de plata": no hay
  // cobro que proyectar (ya se registro por otro lado). Las de cuenta
  // corriente tampoco tienen medio de pago, pero entran por su receivable.
  if (sale.origen === 'administrativa' && !sale.payment_method_id) return false;
  return true;
};

// ---------------------------------------------------------------------------
// Normalización: filas crudas → FlowMovement[]  (PURO, testeable)
// ---------------------------------------------------------------------------

/**
 * Convierte el resultado de todas las queries en una lista única y deduplicada
 * de FlowMovement, ya filtrada al rango [rangeStart, rangeEnd] por fecha canónica.
 *
 * @param {Object} sources  filas crudas por fuente + deferredSaleIds (Set)
 * @param {{rangeStart:string, rangeEnd:string}} bounds  'YYYY-MM-DD'
 * @returns {FlowMovement[]}
 */
export function buildFlowMovements(sources = {}, bounds = {}) {
  const {
    sales = [],
    receivables = [],
    proyecciones = [],
    movimientos = [],
    gastos = [],
    compras = [],
    crmProjects = [],
    crmMilestones = [],
    crmExpenses = [],
    deferredSaleIds = new Set(),
  } = sources;

  const rangeStart = bounds.rangeStart || '0000-01-01';
  const rangeEnd = bounds.rangeEnd || '9999-12-31';
  const inRange = (dateOnly) => !!dateOnly && dateOnly >= rangeStart && dateOnly <= rangeEnd;

  const out = [];

  // 1. Ventas (real) por fecha de acreditación. Las que tienen receivable se
  //    excluyen: se cuentan como receivable para no duplicar. Las anuladas,
  //    desactivadas y los presupuestos no son ingreso.
  for (const sale of sales) {
    if (!isFlowRelevantSale(sale)) continue;
    if (deferredSaleIds.has(sale.id)) continue;
    const date = getBuenosAiresDateOnly(getSaleSettlementDate(sale));
    if (!inRange(date)) continue;
    out.push({
      id: `venta:${sale.id}`,
      source: 'venta',
      direction: 'in',
      state: 'real',
      amount: normalizeMoney(getSaleSettlementAmount(sale)),
      date,
      concept: firstOf(sale.clients)?.full_name || 'Venta POS / Factura',
      category: 'Ventas',
      entityId: firstOf(sale.clients)?.id || null,
      entityName: firstOf(sale.clients)?.full_name || null,
      bankId: sale.payment_methods?.bank_id || null,
      sourceRowId: sale.id,
      isReconcilable: false,
    });
  }

  // 2. Cuentas por cobrar (proyectado) por due_date.
  for (const r of receivables) {
    const date = getBuenosAiresDateOnly(r.due_date);
    if (!inRange(date)) continue;
    out.push({
      id: `receivable:${r.id}`,
      source: 'receivable',
      direction: 'in',
      state: 'proyectado',
      amount: normalizeMoney(r.pending_amount),
      date,
      concept: r.description || 'Cuenta a cobrar',
      category: 'Cuentas por cobrar',
      entityId: r.client_id || null,
      entityName: null,
      bankId: null,
      sourceRowId: r.id,
      isReconcilable: true,
    });
  }

  // 3. Proyecciones manuales (proyectado).
  for (const p of proyecciones) {
    const date = getBuenosAiresDateOnly(p.fecha);
    if (!inRange(date)) continue;
    const direction = p.tipo === 'ingreso' ? 'in' : 'out';
    const scopeLabel = p.sitio_id ? 'Proyección (Sitio)' : 'Proyección (Consolidado)';
    out.push({
      id: `proyeccion:${p.id}`,
      source: 'proyeccion',
      direction,
      state: 'proyectado',
      amount: normalizeMoney(p.monto),
      date,
      concept: p.concepto || 'Proyección',
      category: direction === 'out' ? `Proyección (${p.categoria || 'Gasto'})` : scopeLabel,
      entityId: p.entidad_id || null,
      entityName: null,
      bankId: null,
      sourceRowId: p.id,
      isReconcilable: true,
      // Detalle para el alta/edición y la precarga al confirmar.
      meta: {
        serieId: p.serie_id || null,
        entidadTipo: p.entidad_tipo || null,
        categoria: p.categoria || null,
      },
    });
  }

  // 4. Movimientos financieros (real). Se descartan espejos de venta y de gasto.
  for (const m of movimientos) {
    const date = getBuenosAiresDateOnly(m.fecha_operacion);
    if (!inRange(date)) continue;
    const tipo = String(m.tipo || '').toLowerCase();
    const isIn = tipo.includes('ing') || m.categoria === 'Cobro';
    if (isIn) {
      if (isSaleMirrorCategory(m.categoria)) continue; // ya contado desde sales
      out.push({
        id: `movimiento:${m.id}`,
        source: 'movimiento',
        direction: 'in',
        state: 'real',
        amount: Math.abs(normalizeMoney(m.monto)),
        date,
        concept: m.descripcion || m.categoria || 'Ingreso',
        category: m.categoria || 'Ingresos Fin.',
        entityId: m.entidad_id || null,
        entityName: null,
        bankId: m.bank_id || null,
        sourceRowId: m.id,
        isReconcilable: false,
      });
    } else {
      if (isGastoMirrorMovement(m)) continue; // ya contado desde gastos (dedup exacto)
      out.push({
        id: `movimiento:${m.id}`,
        source: 'movimiento',
        direction: 'out',
        state: 'real',
        amount: Math.abs(normalizeMoney(m.monto)),
        date,
        concept: m.descripcion || m.categoria || 'Egreso',
        category: m.categoria || 'Egresos Fin.',
        entityId: m.entidad_id || null,
        entityName: null,
        bankId: m.bank_id || null,
        sourceRowId: m.id,
        isReconcilable: false,
      });
    }
  }

  // 5. Gastos (real) por fecha.
  //    Los gastos a cuenta corriente no mueven fondos: la salida real la
  //    representa el pago al proveedor (movimiento 'Pago'), así no se duplica.
  for (const g of gastos) {
    if (g.condicion_pago === 'cuenta_corriente') continue;
    const date = getBuenosAiresDateOnly(g.fecha);
    if (!inRange(date)) continue;
    out.push({
      id: `gasto:${g.id}`,
      source: 'gasto',
      direction: 'out',
      state: 'real',
      amount: normalizeMoney(g.monto),
      date,
      concept: g.concepto || 'Gasto del sistema',
      category: getExpenseCategoryLabel(g.categoria),
      entityId: null,
      entityName: null,
      bankId: null,
      sourceRowId: g.id,
      isReconcilable: false,
    });
  }

  // 6. Compras PENDIENTES (proyectado). Las pagadas las representa su movimiento
  //    de pago (real); las anuladas y los comprobantes 'Pago' se excluyen.
  for (const c of compras) {
    const status = normalizeText(c.estado);
    if (CANCELLED_PURCHASE_STATUSES.has(status)) continue;
    if (PAID_PURCHASE_STATUSES.has(status)) continue;
    if (normalizeText(c.tipo_comprobante) === 'pago') continue;

    const isContado = normalizeText(c.condicion_pago).includes('contado');
    const emission = getBuenosAiresDateOnly(c.fecha || c.created_at);
    const due = getPurchaseDueDate(c);
    const date = isContado ? emission : due;
    if (!inRange(date)) continue;

    // Proyectar SOLO el remanente impago. Una compra 'Parcial' ya generó un
    // movimiento real de Pago por lo abonado; proyectar el total_general contaba
    // esa porción dos veces. Bug T5. (Pendiente sin pagos: monto_pagado=0 -> total.)
    const remaining = Math.max(0, normalizeMoney(c.total_general) - normalizeMoney(c.monto_pagado));
    if (remaining <= 0) continue;

    out.push({
      id: `compra:${c.id}`,
      source: 'compra',
      direction: 'out',
      state: 'proyectado',
      amount: remaining,
      date,
      concept: `${c.proveedor_nombre || 'Sin Proveedor'}${c.numero_comprobante ? ` · Nº ${c.numero_comprobante}` : ''}`,
      category: c.tipo_comprobante || 'Compra',
      entityId: c.proveedor_id || null,
      entityName: c.proveedor_nombre || 'Sin Proveedor',
      bankId: null,
      sourceRowId: c.id,
      isReconcilable: true,
      // Detalle específico de compra para el panel "Compras y Vencimientos".
      meta: {
        emissionDate: emission,
        dueDate: due,
        voucherNumber: c.numero_comprobante || '-',
        voucherType: c.tipo_comprobante || 'Compra',
        status: c.estado || null,
      },
    });
  }

  // 7. Liquidaciones CRM — proyectos (proyectado).
  for (const proj of crmProjects) {
    const amount = normalizeMoney(proj.budget_amount);
    if (amount <= 0) continue;
    const date = getBuenosAiresDateOnly(proj.liquidation_due_date);
    if (!inRange(date)) continue;
    const clientName = firstOf(proj.clients)?.full_name || '';
    out.push({
      id: `crm_proyecto:${proj.id}`,
      source: 'crm_proyecto',
      direction: 'in',
      state: 'proyectado',
      amount,
      date,
      concept: `${proj.name || 'Proyecto CRM'}${clientName ? ` - ${clientName}` : ''}`,
      category: 'Liquidación CRM',
      entityId: proj.client_id || null,
      entityName: clientName || null,
      bankId: null,
      sourceRowId: proj.id,
      isReconcilable: true,
    });
  }

  // 8. Liquidaciones CRM — hitos (proyectado).
  for (const mile of crmMilestones) {
    const amount = normalizeMoney(mile.amount);
    if (amount <= 0) continue;
    const date = getBuenosAiresDateOnly(mile.due_date);
    if (!inRange(date)) continue;
    const projectRow = firstOf(mile.crm_projects);
    const clientName = firstOf(projectRow?.clients)?.full_name || '';
    out.push({
      id: `crm_hito:${mile.id}`,
      source: 'crm_hito',
      direction: 'in',
      state: 'proyectado',
      amount,
      date,
      concept: `${mile.name || 'Hito CRM'} - ${projectRow?.name || 'Proyecto'}${clientName ? ` (${clientName})` : ''}`,
      category: 'Liquidación CRM',
      entityId: projectRow?.client_id || null,
      entityName: clientName || null,
      bankId: null,
      sourceRowId: mile.id,
      isReconcilable: true,
    });
  }

  // 9. Gastos directos CRM (real).
  for (const exp of crmExpenses) {
    const amount = normalizeMoney(exp.amount);
    if (amount <= 0) continue;
    const date = getBuenosAiresDateOnly(exp.expense_date);
    if (!inRange(date)) continue;
    const projName = firstOf(exp.crm_projects)?.name;
    out.push({
      id: `crm_gasto:${exp.id}`,
      source: 'crm_gasto',
      direction: 'out',
      state: 'real',
      amount,
      date,
      concept: `${exp.description || 'Gasto CRM'}${projName ? ` (${projName})` : ''}`,
      category: exp.category || 'Gasto CRM',
      entityId: null,
      entityName: null,
      bankId: null,
      sourceRowId: exp.id,
      isReconcilable: false,
    });
  }

  return out;
}

// ---------------------------------------------------------------------------
// Agregadores (puros)
// ---------------------------------------------------------------------------

/** Totales del período, separando real vs proyectado. */
export function summarize(movements = []) {
  let realIn = 0;
  let realOut = 0;
  let projIn = 0;
  let projOut = 0;
  for (const m of movements) {
    const amt = normalizeMoney(m.amount);
    if (m.direction === 'in') {
      if (m.state === 'real') realIn += amt;
      else projIn += amt;
    } else if (m.state === 'real') realOut += amt;
    else projOut += amt;
  }
  const totalIn = realIn + projIn;
  const totalOut = realOut + projOut;
  return { realIn, realOut, projIn, projOut, totalIn, totalOut, net: totalIn - totalOut };
}

/**
 * Agrupa por día del mes (1..31). Devuelve la estructura que consume el
 * calendario: { [day]: { ingresos, egresos, ingProj, gasProj, movs[] } }.
 */
export function groupByDay(movements = []) {
  const map = {};
  const ensure = (day) => {
    if (!map[day]) map[day] = { ingresos: 0, egresos: 0, ingProj: 0, gasProj: 0, movs: [] };
    return map[day];
  };
  for (const m of movements) {
    const day = Number(String(m.date).slice(8, 10));
    if (!Number.isFinite(day) || day < 1) continue;
    const bucket = ensure(day);
    const amt = normalizeMoney(m.amount);
    if (m.direction === 'in') {
      if (m.state === 'real') bucket.ingresos += amt;
      else bucket.ingProj += amt;
    } else if (m.state === 'real') bucket.egresos += amt;
    else bucket.gasProj += amt;

    bucket.movs.push({
      id: m.id,
      type: m.direction,
      isProj: m.state === 'proyectado',
      isManualProjection: m.source === 'proyeccion',
      isReconcilable: m.isReconcilable,
      concept: m.concept,
      category: m.category,
      amount: amt,
      date: m.date,
      source: m.source,
      sourceRowId: m.sourceRowId,
      entityId: m.entityId,
      entityName: m.entityName,
      meta: m.meta,
    });
  }
  return map;
}

/** Agrupa por mes (0..11) → array de 12 con {mes, nombre, ingresos, egresos}. */
export function groupByMonth(movements = []) {
  const stats = MONTH_NAMES.map((nombre, mes) => ({ mes, nombre, ingresos: 0, egresos: 0 }));
  for (const m of movements) {
    const month = Number(String(m.date).slice(5, 7)) - 1;
    if (month < 0 || month > 11) continue;
    const amt = normalizeMoney(m.amount);
    if (m.direction === 'in') stats[month].ingresos += amt;
    else stats[month].egresos += amt;
  }
  return stats;
}

/**
 * Agrupa por entidad para los paneles de Análisis. `direction` = 'in' | 'out'.
 * Devuelve [{ id, name, total, items:[{id, fecha, amount, label, ...}] }].
 */
export function groupByEntity(movements = [], direction = 'in') {
  const map = {};
  for (const m of movements) {
    if (m.direction !== direction) continue;
    const key = m.entityId || `__${m.source}__`;
    const name = m.entityName || m.category || (direction === 'in' ? 'Ingresos' : 'Egresos');
    if (!map[key]) map[key] = { id: key, name, total: 0, items: [] };
    map[key].total += normalizeMoney(m.amount);
    map[key].items.push({
      id: m.id,
      fecha: m.date,
      amount: normalizeMoney(m.amount),
      label: m.concept,
      source: m.source,
      state: m.state,
    });
  }
  return Object.values(map)
    .sort((a, b) => b.total - a.total)
    .map((g) => ({
      ...g,
      items: g.items.sort(
        (a, b) =>
          new Date(`${b.fecha}T12:00:00`).getTime() - new Date(`${a.fecha}T12:00:00`).getTime(),
      ),
    }));
}

/**
 * Saldo acumulado por día a partir de un saldo inicial y el mapa de groupByDay.
 * @returns {{ [day:number]: number }}
 */
export function computeRunningBalance(dayMap = {}, saldoInicial = 0, daysInMonth = 31) {
  const result = {};
  let running = normalizeMoney(saldoInicial);
  for (let d = 1; d <= daysInMonth; d++) {
    const info = dayMap[d] || { ingresos: 0, egresos: 0, ingProj: 0, gasProj: 0 };
    const delDia =
      normalizeMoney(info.ingresos) + normalizeMoney(info.ingProj) -
      (normalizeMoney(info.egresos) + normalizeMoney(info.gasProj));
    running += delDia;
    result[d] = running;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Scoping por sitio (Supabase query builders)
// ---------------------------------------------------------------------------

const applySaleSiteScope = (query, siteId) =>
  siteId
    ? query.or(`site_id.eq.${siteId},and(site_id.is.null,billing_source.in.(${LEGACY_CRM_SOURCE_FILTER}))`)
    : query;

const applyReceivableSiteScope = (query, siteId) =>
  siteId
    ? query.or(`site_id.eq.${siteId},and(site_id.is.null,source.in.(${LEGACY_CRM_SOURCE_FILTER}))`)
    : query;

const applyNullableSiteScope = (query, siteId) =>
  siteId ? query.or(`sitio_id.eq.${siteId},sitio_id.is.null`) : query;

const applyStrictSiteScope = (query, siteId, field = 'sitio_id') =>
  siteId ? query.eq(field, siteId) : query;

// ---------------------------------------------------------------------------
// Fetch (impuro). Recibe supabaseClient por parámetro.
// ---------------------------------------------------------------------------

async function fetchDeferredSaleIds(supabaseClient, orgId, saleIds = []) {
  const valid = (saleIds || []).filter(Boolean);
  if (!valid.length) return new Set();
  const chunks = [];
  for (let i = 0; i < valid.length; i += 200) chunks.push(valid.slice(i, i + 200));

  const results = await Promise.all(
    chunks.map((chunk) =>
      fetchAllPaginated(() =>
        supabaseClient
          .from('sales_receivables')
          .select('id, sale_id')
          .eq('organization_id', orgId)
          .in('sale_id', chunk)
          .order('id', { ascending: true }),
      ),
    ),
  );
  const deferred = new Set();
  for (const rows of results) rows.forEach((row) => row?.sale_id && deferred.add(row.sale_id));
  return deferred;
}

/**
 * Trae y normaliza TODOS los movimientos de flujo de un rango. Única puerta a
 * Supabase del módulo. Las vistas consumen el resultado con los agregadores.
 *
 * @param {Object}  params
 * @param {Object}  params.supabaseClient
 * @param {string}  params.organizationId
 * @param {string}  [params.siteId]
 * @param {string}  params.fromDate  'YYYY-MM-DD'
 * @param {string}  params.toDate    'YYYY-MM-DD'
 * @returns {Promise<FlowMovement[]>}
 */
export async function getFlowMovements({ supabaseClient, organizationId, siteId = null, fromDate, toDate }) {
  const orgId = organizationId;

  // --- Config previa para ventanas de lookback ---
  const [{ data: methodsData }, { data: paymentConditionsData }] = await Promise.all([
    supabaseClient
      .from('payment_methods')
      .select('accreditation_days, accreditation_business_days')
      .eq('organization_id', orgId)
      .neq('is_active', false),
    supabaseClient
      .from('payment_conditions')
      .select('days_until_due')
      .eq('organization_id', orgId)
      .neq('is_active', false),
  ]);

  // Los limites de la query van en UTC, pero la fecha canonica del movimiento se
  // resuelve en horario de Buenos Aires (getBuenosAiresDateOnly) y el corte fino
  // lo hace inRange() mas abajo. Si el limite superior fuera el mismo dia en UTC,
  // una venta del 31/07 22:30 ART (= 01/08 01:30 UTC) quedaria fuera de la query
  // aunque su fecha local sea 31/07, y desapareceria del mes.
  // Por eso se pide un dia extra de cada lado: traer de mas es inocuo, lo que se
  // pase de rango lo descarta inRange().
  const salesLookback = estimateSettlementLookbackDays(methodsData || []);
  const salesFrom = new Date(`${shiftDateOnly(fromDate, -1) || fromDate}T00:00:00.000Z`);
  salesFrom.setUTCDate(salesFrom.getUTCDate() - salesLookback);
  const salesFromISO = salesFrom.toISOString();
  const salesToISO = `${shiftDateOnly(toDate, 1) || toDate}T23:59:59.999Z`;

  const maxPurchaseDays = Math.max(
    180,
    ...(paymentConditionsData || []).map((c) => Math.max(0, Number(c?.days_until_due) || 0)),
  );
  const comprasDesde = shiftDateOnly(fromDate, -maxPurchaseDays) || fromDate;
  // límite superior exclusivo = primer día del mes/rango siguiente
  const comprasHasta = shiftDateOnly(toDate, 1) || toDate;

  // --- Queries (todas paginadas, en paralelo) ---
  const buildSales = () =>
    applySaleSiteScope(
      supabaseClient
        .from('sales')
        .select('id, total, created_at, site_id, status, active, invoice_status, origen, payment_method_id, clients(id, full_name), payment_methods(bank_id, accreditation_days, accreditation_business_days, commission_percentage)')
        .eq('organization_id', orgId)
        .gte('created_at', salesFromISO)
        .lte('created_at', salesToISO)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildReceivables = () =>
    applyReceivableSiteScope(
      supabaseClient
        .from('sales_receivables')
        .select('id, sale_id, due_date, pending_amount, site_id, description, client_id')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .gt('pending_amount', 0)
        .gte('due_date', fromDate)
        .lte('due_date', toDate)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildProyecciones = () =>
    applyNullableSiteScope(
      supabaseClient
        .from('proyecciones')
        .select('id, concepto, monto, fecha, tipo, sitio_id, categoria, serie_id, entidad_id, entidad_tipo')
        .eq('organization_id', orgId)
        .gte('fecha', fromDate)
        .lte('fecha', toDate)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildMovimientos = () =>
    applyNullableSiteScope(
      supabaseClient
        .from('movimientos_financieros')
        .select('id, tipo, categoria, monto, descripcion, fecha_operacion, sitio_id, bank_id, entidad_id, documento_tipo, documento_id')
        .eq('organization_id', orgId)
        // fecha_operacion es timestamptz: se pide un dia extra de cada lado por
        // el desfasaje UTC vs Buenos Aires. El corte exacto lo hace inRange()
        // sobre la fecha local. Sin esto, un movimiento del ultimo dia del mes
        // cargado despues de las 21:00 ART cae en el dia UTC siguiente y se pierde.
        .gte('fecha_operacion', `${shiftDateOnly(fromDate, -1) || fromDate}T00:00:00`)
        .lte('fecha_operacion', `${shiftDateOnly(toDate, 1) || toDate}T23:59:59`)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildGastos = () =>
    applyNullableSiteScope(
      supabaseClient
        .from('gastos')
        .select('id, monto, concepto, fecha, categoria, sitio_id, condicion_pago')
        .eq('organization_id', orgId)
        .not('is_active', 'is', false)
        .gte('fecha', fromDate)
        .lte('fecha', toDate)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildCompras = () =>
    applyStrictSiteScope(
      supabaseClient
        .from('compras')
        .select('id, proveedor_id, proveedor_nombre, total_general, monto_pagado, fecha, created_at, tipo_comprobante, numero_comprobante, estado, condicion_pago, sitio_id')
        .eq('organization_id', orgId)
        .gte('fecha', comprasDesde)
        .lt('fecha', comprasHasta)
        .order('id', { ascending: true }),
      siteId,
    );

  const buildCrmProjects = () =>
    supabaseClient
      .from('crm_projects')
      .select('id, name, budget_amount, liquidation_due_date, client_id, clients(full_name)')
      .eq('organization_id', orgId)
      .not('liquidation_due_date', 'is', null)
      .is('liquidated_sale_id', null)
      .gte('liquidation_due_date', fromDate)
      .lte('liquidation_due_date', toDate)
      .order('id', { ascending: true });

  const buildCrmMilestones = () =>
    supabaseClient
      .from('crm_project_milestones')
      .select('id, name, amount, due_date, project_id, crm_projects(name, client_id, clients(full_name))')
      .eq('organization_id', orgId)
      .eq('status', 'pendiente')
      .gte('due_date', fromDate)
      .lte('due_date', toDate)
      .order('id', { ascending: true });

  const buildCrmExpenses = () =>
    supabaseClient
      .from('crm_project_expenses')
      .select('id, description, amount, expense_date, category, project_id, crm_projects(name)')
      .eq('organization_id', orgId)
      .gte('expense_date', fromDate)
      .lte('expense_date', toDate)
      .order('id', { ascending: true });

  const [
    sales,
    receivables,
    proyecciones,
    movimientos,
    gastos,
    compras,
    crmProjects,
    crmMilestones,
    crmExpenses,
  ] = await Promise.all([
    fetchAllPaginated(buildSales),
    fetchAllPaginated(buildReceivables),
    fetchAllPaginated(buildProyecciones),
    fetchAllPaginated(buildMovimientos),
    fetchAllPaginated(buildGastos),
    fetchAllPaginated(buildCompras),
    fetchAllPaginated(buildCrmProjects),
    fetchAllPaginated(buildCrmMilestones),
    fetchAllPaginated(buildCrmExpenses),
  ]);

  const deferredSaleIds = await fetchDeferredSaleIds(
    supabaseClient,
    orgId,
    sales.map((s) => s.id),
  );

  return buildFlowMovements(
    {
      sales,
      receivables,
      proyecciones,
      movimientos,
      gastos,
      compras,
      crmProjects,
      crmMilestones,
      crmExpenses,
      deferredSaleIds,
    },
    { rangeStart: fromDate, rangeEnd: toDate },
  );
}

export const __MONTH_NAMES = MONTH_NAMES;
